import React, { FC, MouseEvent, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Button } from '@microservices/wiskey-react-components'
import { DataGrid, GridCellParams, MuiEvent } from '@microservices/wiskey-react-table-component'
import { GridApiPro } from '@microservices/wiskey-react-table-component/dist/DataGrid/DataGridV7/models/gridApiPro'
import { Box, GlobalStyles, Grid, Skeleton, Typography } from '@mui/material'
import {
  SearchAssistant,
  SearchAssistantConsumer,
  transformFiltersToValuesPOST,
} from '@widgets/SearchAssistant'

import { CustomColumnMenuComponent } from '@pages/ConfiguredEntity/components/CustomColumnMenuComponent'

import { PageTitle } from '@components/PageTitle'
import { RedirectToConfigButton } from '@components/RedirectToConfigButton'
import { useTimeLineGrid } from '@components/TimeLineGrid/hooks'

import {
  ACTION_CODE,
  BOTTOM_PINNED_TOTAL_ROW_ID,
  CriteriaType,
  ENTITY,
  EVENT_CODE,
  FLEXIBLE,
  FORM_TYPE,
  TIME_LINE_MODE,
  TIME_LINE_OBJECT_X_CELL_DRAG_SOURCE_STYLE,
  TIME_LINE_OBJECT_X_CELL_DRAG_TARGET_STYLE,
  TIME_LINE_OBJECT_X_CELL_MIN_HEIGHT,
} from '@constants'
import { ApiRestrictionData, DataGridRefType, SearchAssistantType, TimeLineDTOType } from '@types'

import { getRSqlBySearch } from '../../helpers/SAFiltersToRSQL'

import { DragGhostOverlay } from './components/DragGhostOverlay'
import { Filters } from './components/Filters'
import { SelectedCellsCount } from './components/SelectedCellsCount'
import { UpdateButton } from './components/UpdateButton'
import { ViewOrEditTimeLineItemModal } from './components/ViewOrEditTimeLineItemModal'
import { isBottomPinnedTotalRow, isGroupTitleRow } from './helpers'

type TimeLineGridProps = {
  config: TimeLineDTOType
  searchAssistantConfig?: SearchAssistantType
  objectXInitialRestrictions: ApiRestrictionData
  objectYInitialRestrictions: ApiRestrictionData
}

export const TimeLineGrid: FC<TimeLineGridProps> = props => {
  const { config, searchAssistantConfig } = props

  const { id } = useParams()

  const dataGridRef = useRef<GridApiPro>(null)

  const {
    columns,
    pinnedColumns,
    handleFetchNextPage,
    totalPages,
    columnsStyles,
    totalCountElements,
    state,
    handlers,
    data,
    columnGroupingModel,
    pinnedRows,
  } = useTimeLineGrid({
    apiRef: dataGridRef as DataGridRefType,
    ...props,
  })

  const [collapsedGroups, setCollapsedGroups] = useState<string[]>([])

  const {
    methods,
    timeLineGridData,
    isLoadingTimeLineGridData,
    sortModel,
    watchDateRange,
    openedViewConfig,
    objectYSAFiltersCriteria,
    objectXSAFiltersCriteria,
  } = state

  const { isDateIntervalsChanged } = data

  const {
    handleUpdateDateIntervals,
    handleOpenForm,
    handleColumnWidthChange,
    handleResetRestrictions,
    handleChangeModelSort,
    handleChangeIsNeedWaitFirstLoadWithSA,
    handleResetDateFilters,
    handleChangeObjectYSAFiltersCriteria,
    handleExecuteActionWithSetVariables,
    handleExecuteActions,
    handleCloseView,
    handleClearSelectedCells,
    handleChangeObjectXSARSQLFilter,
    handleChangeObjectYSARSQLFilter,
    handleChangeObjectXSAFiltersCriteria,
    handleChangeObjectXColumnsObjectSARSQLFilter,
    handleChangeObjectXColumnsObjectSAFiltersCriteria,
    handleChangeIsLoadingColumnsObjectData,
    refreshTotals,
    handleSetIsUseMainObjectXBySAFilter,
    handleChangeIsFilterColumnsObjectByObjectXFilter,
  } = handlers

  const { t } = useTranslation()

  const scrollToTop = () => {
    if (dataGridRef.current) {
      dataGridRef.current.scrollToIndexes({ rowIndex: 0, colIndex: 0 })
    }
  }

  const handleApplyButton = () => {
    scrollToTop()

    const newDateIntervals = handleUpdateDateIntervals(watchDateRange)

    refreshTotals({
      dateIntervals: newDateIntervals,
      objectXSAFiltersCriteria,
      objectYSAFiltersCriteria,
    })

    handleExecuteActionWithSetVariables({ eventCode: EVENT_CODE.ON_SEARCH })
  }

  const handleSearchFilter = async (criterias: CriteriaType[]) => {
    const saFilter = transformFiltersToValuesPOST(criterias)

    const objectYSAFiltersCriteria = saFilter?.filter(filter => {
      return filter.objectCode === config.objectY.objectCode
    })
    const objectXSAFiltersCriteria = saFilter?.filter(filter => {
      return filter.objectCode === config.objectX.objectCode
    })
    const objectXColumnsObjectSAFiltersCriteria = saFilter?.filter(filter => {
      return filter.objectCode === config.objectX.columnsObject?.objectCode
    })

    const objectYRSQLSAFilter = getRSqlBySearch(objectYSAFiltersCriteria)
    const objectXRSQLSAFilter = getRSqlBySearch(objectXSAFiltersCriteria)
    const objectXColumnsObjectRSQLSAFilter = getRSqlBySearch(objectXColumnsObjectSAFiltersCriteria)

    handleChangeObjectXSARSQLFilter(objectXRSQLSAFilter)
    handleChangeObjectYSARSQLFilter(objectYRSQLSAFilter)
    handleChangeObjectXColumnsObjectSARSQLFilter(objectXColumnsObjectRSQLSAFilter)
    handleChangeObjectYSAFiltersCriteria(objectYSAFiltersCriteria)
    handleChangeObjectXSAFiltersCriteria(objectXSAFiltersCriteria)
    handleChangeObjectXColumnsObjectSAFiltersCriteria(objectXColumnsObjectSAFiltersCriteria)

    handleExecuteActionWithSetVariables({
      objectYSAFiltersCriteria,
      objectXSAFiltersCriteria,
      objectXColumnsObjectSAFiltersCriteria,
      eventCode: EVENT_CODE.ON_SEARCH,
    })
    handleChangeIsNeedWaitFirstLoadWithSA(false)

    if (config.mode === TIME_LINE_MODE.DATE_TIME) {
      const newDateIntervals = handleUpdateDateIntervals(watchDateRange)

      refreshTotals({
        dateIntervals: newDateIntervals,
        objectXSAFiltersCriteria,
        objectYSAFiltersCriteria,
      })
    }

    if (config.mode === TIME_LINE_MODE.OBJECT) {
      handleChangeIsLoadingColumnsObjectData(true)
    }

    handleSetIsUseMainObjectXBySAFilter(
      Boolean(objectXRSQLSAFilter) && Boolean(config.filterObjectYByObjectX)
    )

    handleChangeIsFilterColumnsObjectByObjectXFilter(
      Boolean(objectXRSQLSAFilter) && Boolean(config.filterColumnsObjectByObjectX)
    )
  }

  const handleResetSearchFilter = () => {
    handleSearchFilter([])
  }

  const toggleGroupRow = (params: GridCellParams) => {
    if (collapsedGroups.includes(params.row.id)) {
      setCollapsedGroups(collapsedGroups.filter(item => item !== params.row.id))

      return
    }

    setCollapsedGroups(collapsedGroups.concat(params.row.id as string))
  }

  const handleCellClick = (params: GridCellParams, event: MuiEvent<React.MouseEvent>) => {
    if (window.getSelection()?.type === 'Range') {
      return
    }

    event.stopPropagation()

    if (isBottomPinnedTotalRow(params.id as string)) {
      return
    }

    if (isGroupTitleRow(params.row.id)) {
      toggleGroupRow(params)

      return
    }
  }

  const handleButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (!config.objectX.actions?.length) {
      return
    }

    handleExecuteActions({
      actions: config.objectX.actions,
      eventCode: EVENT_CODE.TIME_LINE_ON_CREATE_BUTTON_CLICK,
      srcObj: {
        formProps: {
          event: FORM_TYPE.CREATE,
          viewCode: '',
          title: '',
          id: '',
          objectCode: '',
          path: '',
          entityType: ENTITY.VIEW,
          isIndependentForm: true,
        },
        event,
        additionalProps: {
          addHistory: true,
        },
      },
    })
  }

  const renderCreateObjectXRecordButton = () => {
    const action = config.objectX.actions?.find(
      action => action.eventCode === EVENT_CODE.TIME_LINE_ON_CREATE_BUTTON_CLICK
    )

    if (action) {
      return (
        <Button variant='contained' onClick={handleButtonClick}>
          {action.title}
        </Button>
      )
    }

    return null
  }

  const filteredRows = useMemo(() => {
    if (!collapsedGroups?.length) {
      return timeLineGridData
    }

    return timeLineGridData.filter(row => {
      return !collapsedGroups.includes(row.groupId as string)
    })
  }, [collapsedGroups, timeLineGridData])

  const isSelectionAvailable = useMemo(() => {
    return config.objectX.actions?.some(action => action.actionCode === ACTION_CODE.SELECT_ITEM)
  }, [config])

  const totalCountElement = (
    <Typography fontSize='1rem'>
      {t('searchAssistant.result', { totalElements: totalCountElements })}
    </Typography>
  )

  return (
    <Box
      sx={{
        ...FLEXIBLE,
        m: 2,
      }}
    >
      {config.mode === TIME_LINE_MODE.DATE_TIME && (
        <Filters
          isFiltersChanged={isDateIntervalsChanged}
          isShowTimeIntervalFilter={config?.timeInterval?.showFilter}
          methods={methods}
          watchDateRange={watchDateRange}
          onResetFilters={handleResetDateFilters}
          onSearchClick={handleApplyButton}
        />
      )}

      <PageTitle
        end={
          <Grid container alignItems='center' gap={2}>
            <UpdateButton
              isLoading={isLoadingTimeLineGridData}
              isShowButton={config.showUpdateTimeLineDataButton}
              onClick={handleApplyButton}
            />
            {renderCreateObjectXRecordButton()}
            <RedirectToConfigButton code={id} isOpenNewTab={false} type={'timeline'} />
          </Grid>
        }
        start={
          <Grid container alignItems='center' gap={2}>
            <Typography display={'inline'} variant={'h6'}>
              {config.title}
            </Typography>
            {isLoadingTimeLineGridData ? (
              <Skeleton variant='text'>{totalCountElement}</Skeleton>
            ) : (
              totalCountElement
            )}
          </Grid>
        }
        sx={{
          mb: 1,
        }}
      />

      <GlobalStyles
        styles={[
          columnsStyles,
          TIME_LINE_OBJECT_X_CELL_DRAG_SOURCE_STYLE,
          TIME_LINE_OBJECT_X_CELL_DRAG_TARGET_STYLE,
        ]}
      />
      {Boolean(searchAssistantConfig?.id) && (
        <SearchAssistant
          visible
          consumer={SearchAssistantConsumer.page}
          entityInternalId={config.internalId}
          hasActualTimeWindow={false}
          searchAssistantId={searchAssistantConfig?.id}
          objectCodes={[
            config.objectY.objectCode,
            config.objectX.objectCode,
            ...(config.mode === TIME_LINE_MODE.OBJECT && config.objectX.columnsObject?.objectCode
              ? [config.objectX.columnsObject.objectCode as string]
              : []),
          ]}
          onReset={handleResetSearchFilter}
          onSearchFilter={handleSearchFilter}
        />
      )}

      {isSelectionAvailable && (
        <SelectedCellsCount onClearSelectedCells={handleClearSelectedCells} />
      )}

      <DataGrid
        enableCellSeparator
        enableMultiSort
        hideFooter
        apiRef={dataGridRef as DataGridRefType}
        columnGroupingModel={columnGroupingModel}
        columns={columns}
        disableColumnMenu={false}
        disableColumnReorder={false}
        disableColumnResize={false}
        disableColumnSorting={false}
        disableMultipleColumnsSorting={false}
        fetchNextPage={handleFetchNextPage}
        loading={isLoadingTimeLineGridData}
        pageCount={totalPages}
        paginationModel={{ page: 0, pageSize: 40 }}
        pinnedColumns={pinnedColumns}
        pinnedRows={filteredRows.length ? pinnedRows : undefined}
        rowHeight={config.objectX.cell?.minHeight}
        rows={filteredRows}
        sortModel={sortModel}
        getRowHeight={params => {
          if (params.id?.toString().includes(BOTTOM_PINNED_TOTAL_ROW_ID)) {
            return TIME_LINE_OBJECT_X_CELL_MIN_HEIGHT
          }

          return config.objectX.cell?.minHeight
        }}
        slots={{
          columnMenu: props => (
            <CustomColumnMenuComponent
              {...props}
              apiRef={dataGridRef as DataGridRefType}
              handleResetRestrictions={() => handleResetRestrictions(props)}
              visibleItems={['reset']}
            />
          ),
        }}
        sx={{
          mt: 1,
          '& .MuiDataGrid-columnSeparator': {
            svg: {
              height: 0,
            },
          },
          '& .MuiDataGrid-cell': {
            borderBottom: 'none',
          },
          '& .MuiDataGrid-columnHeader': {
            borderTop: 1,
            borderColor: '#f0f0f0',
          },
          '& .MuiDataGrid-columnHeaderTitleContainer': {
            borderBottomWidth: '0 !important',
          },
          '& .MuiDataGrid-filler--pinnedLeft': {
            borderRight: 'none !important',
          },
          '& .MuiDataGrid-filler--pinnedRight': {
            borderLeft: 'none !important',
          },
        }}
        onCellClick={handleCellClick}
        onColumnWidthChange={handleColumnWidthChange}
        onSortModelChange={handleChangeModelSort}
      />
      {!!openedViewConfig && (
        <ViewOrEditTimeLineItemModal
          config={config}
          data={openedViewConfig}
          isOpen={!!openedViewConfig}
          handlers={{
            state,
            handlers,
            data,
          }}
          onClose={handleCloseView}
          onOpenForm={handleOpenForm}
        />
      )}
      <DragGhostOverlay config={config} />
    </Box>
  )
}
